import {
  type FaceComposite,
  type FaceKey,
  type FacePhotoSize,
  faceCompositeSchema,
} from '@repo/api-contracts';
import { koreanMessageOr } from '@/shared/lib';
import {
  type CompositeAssetKind,
  compositeOnly,
  compositeSlotKey,
  withoutCompositeOperations,
} from '../lib/composite-asset';
import type { EditAreaDraftState } from './edit-area-draft-types';

type Commands = Pick<
  EditAreaDraftState,
  | 'setCompositeAsset'
  | 'removeCompositeAsset'
  | 'setCompositeMultiply'
  | 'beginCompositeOperation'
  | 'setCompositeOperationStatus'
  | 'cancelCompositeOperation'
>;
interface CommandContext {
  readonly get: () => EditAreaDraftState;
  readonly commit: (patch: Partial<EditAreaDraftState>) => void;
  readonly reject: (message: string) => false;
  readonly canEdit: () => boolean;
}

const MASK_REQUIRED = '마스크를 먼저 올려 주세요.';

/**
 * 에셋을 넣은 합성 설정. 마스크는 필수라 음영·안내선은 마스크가 만든 합성 설정에만 더하고, 없으면 null 이다.
 * 첫 마스크로 합성 설정을 만들 때 multiply 는 끈다(Q10). 마스크를 바꿔도 음영·안내선·multiply 는 둔다(Q9).
 * 모양은 맞춰 두지만 키 형식·키와 크기의 짝은 호출자가 faceCompositeSchema 로 검사한다.
 */
const withAsset = (
  composite: FaceComposite | undefined,
  kind: CompositeAssetKind,
  key: string,
  size: FacePhotoSize,
): FaceComposite | null => {
  if (kind === 'mask')
    return { ...(composite ?? { multiply: false }), maskKey: key, maskSize: size };
  if (composite === undefined) return null;
  return kind === 'shading'
    ? { ...composite, shadingKey: key, shadingSize: size }
    : { ...composite, safeAreaKey: key, safeAreaSize: size };
};

const hasAsset = (composite: FaceComposite, kind: CompositeAssetKind): boolean =>
  kind === 'mask' ||
  (kind === 'shading' ? composite.shadingKey !== undefined : composite.safeAreaKey !== undefined);

/** 음영·안내선의 키·크기를 함께 뺀다. 마스크 삭제는 합성 설정 전체 삭제라 여기서 다루지 않는다. */
const withoutAsset = (composite: FaceComposite, kind: 'shading' | 'safeArea'): FaceComposite =>
  compositeOnly(
    kind === 'shading'
      ? { ...composite, shadingKey: undefined, shadingSize: undefined }
      : { ...composite, safeAreaKey: undefined, safeAreaSize: undefined },
  );

/**
 * 면 합성 설정(faces.<면>.composite) 명령과 합성 업로드 작업. 합성 설정에는 키·크기·multiply 만 두고, 표시 URL 은
 * 사진과 같은 URL 맵(photoUrls)에 둔다. 업로드 작업은 사진 작업과 같은 operations 표에 면·종류 칸으로 둔다 —
 * 색상을 지워도 남고, 면을 끄면 그 면 칸이 함께 지워진다.
 */
export const createEditAreaCompositeCommands = ({
  get,
  commit,
  reject,
  canEdit,
}: CommandContext): Commands => {
  // 사진 업로드 시작과 같은 조건이다 — 막혀도 거부 문구를 남기지 않고, 업로드 쪽이 false 를 보고 멈춘다.
  const canStart = (): boolean => !get().locked && !get().isUnsupported;
  const isActiveFace = (face: FaceKey): boolean =>
    get().draft.activeFaces.includes(face) && get().faces[face] !== undefined;
  const isCurrent = (face: FaceKey, kind: CompositeAssetKind, id: string): boolean =>
    isActiveFace(face) && get().operations[compositeSlotKey(face, kind)]?.id === id;
  const withoutOperation = (slot: string): EditAreaDraftState['operations'] =>
    Object.fromEntries(Object.entries(get().operations).filter(([key]) => key !== slot));

  return {
    setCompositeAsset: (face, kind, { key, size, url }, operationId) => {
      // 업로드 훅의 반영 — 낡은 작업·잠금이면 사진 setPhoto 처럼 문구 없이 막고, 훅이 false 를 보고 정리한다.
      if (operationId !== undefined && (!canStart() || !isCurrent(face, kind, operationId)))
        return false;
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current) return reject('사용 중인 면을 선택해 주세요.');
      const next = withAsset(current.composite, kind, key, size);
      if (next === null) return reject(MASK_REQUIRED);
      const parsed = faceCompositeSchema.safeParse(next);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '합성 에셋 값을 확인해 주세요.'),
        );
      commit({
        faces: { ...get().faces, [face]: { ...current, composite: compositeOnly(parsed.data) } },
        photoUrls: { ...get().photoUrls, [key]: url },
        operations: withoutOperation(compositeSlotKey(face, kind)),
      });
      return true;
    },
    removeCompositeAsset: (face, kind) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      const composite = current?.composite;
      if (!current || !composite || !hasAsset(composite, kind))
        return reject('지울 합성 에셋이 없습니다.');
      commit({
        faces: {
          ...get().faces,
          // 마스크는 필수라 마스크를 지우면 그 면 합성 설정 전체를 지운다.
          [face]:
            kind === 'mask'
              ? { surfaces: current.surfaces, placements: current.placements }
              : { ...current, composite: withoutAsset(composite, kind) },
        },
        // 마스크가 빠지면 올라오던 음영·안내선도 둘 곳이 없다 — 그 면 합성 업로드를 함께 취소한다(D-E).
        ...(kind === 'mask'
          ? { operations: withoutCompositeOperations(get().operations, face) }
          : {}),
      });
      return true;
    },
    setCompositeMultiply: (face, multiply) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current?.composite) return reject(MASK_REQUIRED);
      commit({
        faces: {
          ...get().faces,
          [face]: { ...current, composite: { ...current.composite, multiply } },
        },
      });
      return true;
    },
    beginCompositeOperation: (face, kind, id) => {
      const slot = compositeSlotKey(face, kind);
      const operation = get().operations[slot];
      if (!canStart() || !isActiveFace(face) || (operation && operation.status !== 'error'))
        return false;
      commit({ operations: { ...get().operations, [slot]: { id, status: 'uploading' } } });
      return true;
    },
    setCompositeOperationStatus: (face, kind, id, status) => {
      if (!isCurrent(face, kind, id)) return;
      commit({
        operations: { ...get().operations, [compositeSlotKey(face, kind)]: { id, status } },
      });
    },
    cancelCompositeOperation: (face, kind, id) => {
      if (!isCurrent(face, kind, id)) return;
      commit({ operations: withoutOperation(compositeSlotKey(face, kind)) });
    },
  };
};
